import { ProgressCircle, View } from '@adobe/react-spectrum';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormViewComponent } from '../../../../components/protected/layout-components/FormView/FormViewComponent';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { FPADataFactory, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { ListItemValueRequest } from '../../../../services/soap/form/requests/ListItemValueRequest';
import { ListFormsRequest } from '../../../../services/soap/form/requests/ListFormsRequest';
import { Itemvalue, UpdateFormItemValuesRequest } from '../../../../services/soap/form/requests/UpdateFormItemValuesRequest';
import { SoapUtils } from '../../../../utils/SoapUtils';

interface IIssueCustomFormViewProps {
  formId: string;
  formValuesId?: string;
  remoteId?: string;
  remoteAcl?: string;
}

type SubmitOverrides = {
  remoteId?: string;
  remoteAcl?: string;
};

export interface IIssueCustomFormViewHandle {
  submitForm: (overrides?: SubmitOverrides) => Promise<boolean>;
}

const IssueCustomFormView = forwardRef<IIssueCustomFormViewHandle, IIssueCustomFormViewProps>(({ formId, formValuesId, remoteId, remoteAcl }, ref) => {
  const { formService, store } = useDependency();
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [formItemNames, setFormItemNames] = useState<Record<string, string>>({});
  const [componentToFormItem, setComponentToFormItem] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [reservedHeight, setReservedHeight] = useState<number | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const collectFormItemMappings = useCallback((components: any[] = [], acc: Record<string, string> = {}) => {
    components.forEach((component: any) => {
      if (component?.PROPERTIES?.id && component?.FORMITEM) {
        acc[String(component.PROPERTIES.id)] = String(component.FORMITEM);
      }
      if (component?.CHILDREN?.COMPONENT?.length) {
        collectFormItemMappings(component.CHILDREN.COMPONENT, acc);
      }
    });
    return acc;
  }, []);

  const toFormValuesState = useCallback((rows: any[] = []) => {
    const nextValues = rows.reduce((acc: Record<string, any>, row: any) => {
      const itemValue = row?.ITEMVALUE;
      if (itemValue?.formItem) {
        acc[String(itemValue.formItem)] = itemValue.value;
      }
      return acc;
    }, {});

    const nextItemNames = rows.reduce((acc: Record<string, string>, row: any) => {
      const itemValue = row?.ITEMVALUE;
      if (itemValue?.formItem) {
        acc[String(itemValue.formItem)] = String(itemValue.formItemName || itemValue.formItem);
      }
      return acc;
    }, {});

    return { nextValues, nextItemNames };
  }, []);

  const selectedItem = useMemo(
    () =>
      FPADataFactory.createFromTemplateItem({
        key: `issue-form-${formId}`,
        id: Number(formId),
        title: 'Issue custom form',
        type: FPADataTypes.ACTIVITY,
        itemType: 0,
        formId: Number(formId),
        childItems: [],
      }),
    [formId]
  );

  useEffect(() => {
    let isMounted = true;

    const loadValues = async () => {
      setIsLoading(true);
      try {
        const formResponse: any = await formService.listForms(
          new ListFormsRequest(store.Server, store.SessionId, formId)
        );
        const formJson = SoapUtils.parseXmlString2(formResponse?.ROW?.definition, ['COMPONENT']);
        const nextMappings = collectFormItemMappings(formJson?.definition?.gui?.COMPONENT || []);
        if (isMounted) {
          setComponentToFormItem(nextMappings);
        }

        if (!formValuesId) {
          if (remoteId) {
            const remoteResponse: any = await formService.listItemValue(
              new ListItemValueRequest(store.Server, store.SessionId, '', '', '', '', '', remoteId, '')
            );
            if (!isMounted) return;
            const { nextValues, nextItemNames } = toFormValuesState(remoteResponse?.ROWS || []);
            setFormValues(nextValues);
            setFormItemNames(nextItemNames);
          } else if (isMounted) {
            setFormValues({});
            setFormItemNames({});
          }
          return;
        }

        let response: any = null;
        if (remoteId) {
          response = await formService.listItemValue(
            new ListItemValueRequest(store.Server, store.SessionId, '', '', '', '', '', remoteId, '')
          );
        }
        if ((!response?.ROWS || response.ROWS.length === 0) && formValuesId) {
          response = await formService.listItemValue(
            new ListItemValueRequest(store.Server, store.SessionId, formValuesId)
          );
        }
        if ((!response?.ROWS || response.ROWS.length === 0) && remoteId) {
          response = await formService.listItemValue(
            new ListItemValueRequest(store.Server, store.SessionId, '', '', '', '', formId, remoteId, 'issue')
          );
        }
        if (!isMounted) return;

        const { nextValues, nextItemNames } = toFormValuesState(response?.ROWS || []);
        setFormValues(nextValues);
        setFormItemNames(nextItemNames);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadValues();

    return () => {
      isMounted = false;
    };
  }, [collectFormItemMappings, formId, formService, formValuesId, remoteId, store.Server, store.SessionId, toFormValuesState]);

  const getFormViewData = useCallback(
    (key: string, defaultValue: any) => {
      const normalizedKey = componentToFormItem[key] || key;
      return Object.prototype.hasOwnProperty.call(formValues, normalizedKey) ? formValues[normalizedKey] : defaultValue;
    },
    [componentToFormItem, formValues]
  );

  const setFormViewData = useCallback((key: string, value: any) => {
    const normalizedKey = componentToFormItem[key] || key;
    setFormValues((prev) => ({ ...prev, [normalizedKey]: value }));
  }, [componentToFormItem]);

  const submitForm = useCallback(async (overrides?: SubmitOverrides) => {
    const resolvedRemoteId = String(overrides?.remoteId || remoteId || '').trim();
    const resolvedRemoteAcl = String(overrides?.remoteAcl || remoteAcl || '').trim();

    if (!resolvedRemoteId) {
      return false;
    }

    const itemValues = Object.entries(formValues).map(([formItem, value]) => {
      const normalizedValue =
        value === null || value === undefined
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);

      return new Itemvalue(formItem, formItemNames[formItem] || formItem, normalizedValue);
    });

    const response: any = await formService.updateFormItemValues(
      new UpdateFormItemValuesRequest(
        store.Server,
        store.SessionId,
        undefined,
        undefined,
        undefined,
        resolvedRemoteId,
        resolvedRemoteAcl,
        'issue',
        itemValues
      )
    );

    return response?.result === 'OK';
  }, [formItemNames, formService, formValues, remoteAcl, remoteId, store.Server, store.SessionId]);

  useImperativeHandle(ref, () => ({
    submitForm,
  }), [submitForm]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || isLoading) {
      return;
    }

    let frameId = 0;

    const measureHeight = () => {
      const currentWrapper = wrapperRef.current;
      if (!currentWrapper) return;

      const wrapperRect = currentWrapper.getBoundingClientRect();
      let deepestBottom = currentWrapper.scrollHeight;
      const descendants = currentWrapper.querySelectorAll('*');

      descendants.forEach((element) => {
        const rect = (element as HTMLElement).getBoundingClientRect();
        const relativeBottom = rect.bottom - wrapperRect.top;
        if (relativeBottom > deepestBottom) {
          deepestBottom = relativeBottom;
        }
      });

      const nextHeight = Math.ceil(deepestBottom);
      setReservedHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(measureHeight);
    };

    scheduleMeasure();

    const resizeObserver = new ResizeObserver(scheduleMeasure);
    resizeObserver.observe(wrapper);
    wrapper.querySelectorAll('*').forEach((element) => resizeObserver.observe(element));

    const mutationObserver = new MutationObserver(scheduleMeasure);
    mutationObserver.observe(wrapper, { childList: true, subtree: true, attributes: true, characterData: true });

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
    };
  }, [componentToFormItem, formId, formValues, formValuesId, isLoading]);

  if (isLoading) {
    return (
      <div className="issue-full-width-form">
        <View padding="size-200">
          <ProgressCircle aria-label="Loading form" isIndeterminate />
        </View>
      </div>
    );
  }

  return (
    <div className="issue-full-width-form" ref={wrapperRef} style={reservedHeight ? { minHeight: `${reservedHeight}px` } : undefined}>
      <FormViewComponent
        key={`issue-custom-form-${formId}-${formValuesId || 'empty'}`}
        formIdType="self"
        formId={formId}
        isNew={true}
        selectedItem={selectedItem}
        getFormViewData={getFormViewData}
        setFormViewData={setFormViewData}
      />
    </div>
  );
});

export default IssueCustomFormView;
